
import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import petSpecies from '../../data/petSpecies';
import i18n from 'i18next';
import { GoogleMap, Marker, InfoWindow, Autocomplete } from '@react-google-maps/api';
import { HiAdjustmentsHorizontal } from 'react-icons/hi2';
import '../../style/FoundPetsPage.css';

const MAP_CONTAINER_STYLE = { width: '100%', height: '500px' };
const DEFAULT_CENTER = { lat: 41.3851, lng: 2.1734 };

const FoundPetsPage = () => {
    const { t } = useTranslation();
    const [foundPets, setFoundPets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [view, setView] = useState('list');
    const [filtersVisible, setFiltersVisible] = useState(false);
    const [selectedPet, setSelectedPet] = useState(null);
    const [selectedLocation, setSelectedLocation] = useState(null);
    const [selectedDateRange, setSelectedDateRange] = useState(null);
    const [selectedPetType, setSelectedPetType] = useState('');
    const [showOnlyMyPets, setShowOnlyMyPets] = useState(false);
    const autocompleteRef = useRef(null);
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

    // Get logged-in user
    const storedUser = JSON.parse(localStorage.getItem('user'));
    const userId = storedUser?._id;

    useEffect(() => {
        const fetchFoundPets = async () => {
            try {
                const response = await axios.get(`${baseUrl}/api/found-pets`);
                setFoundPets(response.data);
            } catch (err) {
                setError(err.message || 'Error fetching found pets');
            } finally {
                setLoading(false);
            }
        };
        fetchFoundPets();
    }, [baseUrl]);

    const handlePlaceChanged = () => {
        const autocomplete = autocompleteRef.current;
        if (!autocomplete) return;
        const place = autocomplete.getPlace();
        if (place.geometry && place.geometry.location) {
            setSelectedLocation({
                lat: place.geometry.location.lat(),
                lng: place.geometry.location.lng(),
            });
        }
    };

    const dateRanges = [
        { label: t('lostPetsPage.lastDay'), value: 1 },
        { label: t('lostPetsPage.last10Days'), value: 10 },
        { label: t('lostPetsPage.lastMonth'), value: 30 },
        { label: t('lostPetsPage.lastQuarter'), value: 90 },
        { label: t('lostPetsPage.lastYear'), value: 365 },
    ];

    // Helper: get translated pet type
    const getPetTypeLabel = (petTypeValue) => {
        const species = petSpecies.find(species => species.value === petTypeValue);
        return species ? species.label[i18n.language] || species.label.en : t('generalPet.unknown');
    };

    // Helper: days since found
    const getDaysSinceFound = (foundDateTime) => {
        if (!foundDateTime) return null;
        const foundDate = new Date(foundDateTime);
        if (isNaN(foundDate)) return null;
        const now = new Date();
        const diffTime = Math.abs(now - foundDate);
        return Math.floor(diffTime / (1000 * 60 * 60 * 24));
    };

    // Filtering logic
    let filteredPets = foundPets.filter((pet) => {
        // Location filter
        if (selectedLocation && pet.foundLocation && pet.foundLocation.coordinates) {
            const [lng, lat] = pet.foundLocation.coordinates;
            const distance = Math.sqrt(
                Math.pow(lat - selectedLocation.lat, 2) +
                Math.pow(lng - selectedLocation.lng, 2)
            );
            if (distance >= 0.1) return false;
        }
        // Date filter
        if (selectedDateRange) {
            if (!pet.foundDateTime) return false;
            const foundDate = new Date(pet.foundDateTime);
            if (isNaN(foundDate)) return false;
            const daysAgo = new Date();
            daysAgo.setDate(daysAgo.getDate() - selectedDateRange);
            if (foundDate < daysAgo) return false;
        }
        // Pet type filter
        if (selectedPetType && pet.petType && pet.petType.toLowerCase() !== selectedPetType.toLowerCase()) {
            return false;
        }
        return true;
    });
    // Filter by reportedBy if checked and user is logged in
    if (showOnlyMyPets && userId) {
        filteredPets = filteredPets.filter((pet) => pet.reportedBy === userId);
    }

    if (loading) return <p className="text-dafauna-gray">{t('lostPetsPage.loading')}</p>;
    if (error) return <p className="text-red-500">{t('lostPetsPage.errorFetching')}</p>;

    return (
        <div className="found-pets-page">
            <h1 className="text-dafauna-darkText text-2xl font-bold text-center mb-section">{t('foundPetsPage.title', 'Found Pets')}</h1>

            <div className="filters-header">
                <button
                    className="toggle-filters-button"
                    onClick={() => setFiltersVisible(!filtersVisible)}
                >
                    <HiAdjustmentsHorizontal size={20} /> {filtersVisible ? t('lostPetsPage.hideFilters') : t('lostPetsPage.showFilters')}
                </button>
                <button
                    onClick={() => {
                        setSelectedLocation(null);
                        setSelectedDateRange(null);
                        setSelectedPetType('');
                        if (autocompleteRef.current) autocompleteRef.current.value = '';
                    }}
                    className="clear-filters-btn"
                >
                    {t('lostPetsPage.clearFilters')}
                </button>
                <Link
                    to="/report-found-pet"
                    className="bg-dafauna-primary text-white py-2 px-4 rounded-md"
                    style={{ paddingRight: '5px', paddingLeft: '5px' }}
                >
                    {t('foundPetsPage.reportFoundPet', 'Report Pet Found')}
                </Link>
            </div>

            {/* Show only my pets filter if logged in */}
            {userId && (
                <div className="filter-options" style={{ marginBottom: '20px', textAlign: 'center' }}>
                    <label>
                        <input
                            type="checkbox"
                            checked={showOnlyMyPets}
                            onChange={e => setShowOnlyMyPets(e.target.checked)}
                            style={{ marginRight: '10px' }}
                        />
                        {t('foundPetsPage.showOnlyMyPets', 'Show only my reported pets')}
                    </label>
                </div>
            )}

            {filtersVisible && (
                <div className={`filters-container visible`}>
                    <Autocomplete onLoad={ac => (autocompleteRef.current = ac)} onPlaceChanged={handlePlaceChanged}>
                        <input
                            type="text"
                            placeholder={t('lostPetsPage.searchLocationPlaceholder')}
                            onChange={e => { if (!e.target.value) setSelectedLocation(null); }}
                            className="w-full p-2 rounded-md border border-gray-300"
                        />
                    </Autocomplete>
                    <div className="flex items-center gap-2">
                        <label className="text-dafauna-darkText font-medium">{t('lostPetsPage.dateRange')}</label>
                        <select
                            onChange={e => setSelectedDateRange(Number(e.target.value))}
                            className="p-2 rounded-md border border-gray-300"
                        >
                            <option value="">{t('lostPetsPage.selectDateRange')}</option>
                            {dateRanges.map(range => (
                                <option key={range.value} value={range.value}>{range.label}</option>
                            ))}
                        </select>
                    </div>
                    <div className="flex items-center gap-2">
                        <label className="text-dafauna-darkText font-medium">{t('lostPetsPage.petType')}</label>
                        <select
                            onChange={e => setSelectedPetType(e.target.value)}
                            className="p-2 rounded-md border border-gray-300"
                        >
                            <option value="">{t('lostPetsPage.selectPetType')}</option>
                            {petSpecies.map(species => (
                                <option key={species.value} value={species.value}>
                                    {getPetTypeLabel(species.value)}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            )}

            <div className="view-toggle-list-map-floating">
                {view === 'list' ? (
                    <button onClick={() => setView('map')} className="py-2 px-4 rounded-md bg-dafauna-darkText text-white shadow-lg">
                        {t('lostPetsPage.mapView')}
                    </button>
                ) : (
                    <button onClick={() => setView('list')} className="py-2 px-4 rounded-md bg-dafauna-darkText text-white shadow-lg">
                        {t('lostPetsPage.listView')}
                    </button>
                )}
            </div>

            {view === 'list' && (
                <div className="found-pets-list">
                    {filteredPets.map((pet) => {
                        const daysSinceFound = getDaysSinceFound(pet.foundDateTime);
                        return (
                            <div key={pet._id} className="pet-card-found">
                                <div className="pet-card-left">
                                    {Array.isArray(pet.images) && pet.images.length > 0 ? (
                                        <img src={pet.images[0]} alt={pet.name} />
                                    ) : (
                                        <p className="placeholder-image">{t('generalPet.imageAvailable')}</p>
                                    )}
                                </div>
                                <div className="pet-card-right">
                                    <div className="pet-info">
                                        <h2>{getPetTypeLabel(pet.petType)}</h2>
                                    </div>
                                    {daysSinceFound !== null && (
                                        <p>{t('foundPetsPage.daysFound', { count: daysSinceFound })}</p>
                                    )}
                                    {pet.foundLocation?.address && (
                                        <p>{pet.foundLocation.address}</p>
                                    )}
                                    <Link to={`/found-pet/${pet._id}`}>{t('lostPetsPage.viewDetails')}</Link>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {view === 'map' && (
                <GoogleMap
                    mapContainerStyle={MAP_CONTAINER_STYLE}
                    center={selectedLocation || DEFAULT_CENTER}
                    zoom={selectedLocation ? 15 : 10}
                >
                    {filteredPets.map((pet) => {
                        if (!pet.foundLocation || !pet.foundLocation.coordinates) return null;
                        const [lng, lat] = pet.foundLocation.coordinates;
                        return (
                            <Marker
                                key={pet._id}
                                position={{ lat, lng }}
                                onClick={() => setSelectedPet(pet)}
                            >
                                {selectedPet?._id === pet._id && (
                                    <InfoWindow
                                        position={{ lat, lng }}
                                        onCloseClick={() => setSelectedPet(null)}
                                    >
                                        <div style={{ textAlign: 'center' }}>
                                            {Array.isArray(pet.images) && pet.images.length > 0 && (
                                                <img
                                                    src={pet.images[0]}
                                                    alt={pet.name}
                                                    style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '5px' }}
                                                />
                                            )}
                                            <h2 style={{ fontSize: '16px', margin: '10px 0' }}>{pet.name}</h2>
                                            <p>{getPetTypeLabel(pet.petType)}</p>
                                            {pet.foundLocation?.address && <p>{pet.foundLocation.address}</p>}
                                            <Link
                                                to={`/found-pet/${pet._id}`}
                                                style={{
                                                    display: 'inline-block',
                                                    marginTop: '10px',
                                                    color: '#007bff',
                                                    textDecoration: 'none',
                                                }}
                                            >
                                                {t('lostPetsPage.viewDetails')}
                                            </Link>
                                        </div>
                                    </InfoWindow>
                                )}
                            </Marker>
                        );
                    })}
                </GoogleMap>
            )}
        </div>
    );
};

export default FoundPetsPage;